import { addParameterToURL, buildUrl, getUrlParameters } from "@utils/url";
import { User, type UserData } from "./User";

type BaseUrlType = "BM_BASE_DOCS_URL" | "BM_BASE_URL" | "BM_BEFEKTETES_KOCKAZAT_URL" | "BM_BEFEKTETES_PORTFOLIO_URL";

interface ProductData {
	name: string;
	calculatorUrl: string;
}

export interface AppConfigData {
	// Source specific data
	source: string;
	contactEmail: string;
	isPartner: "1" | "0";

	// General data
	showPoweredBy: "1" | "0";
	bmDiscounts: "1" | "0";
	minWage: number;

	// Site specific data
	abVersion: "A" | "B";
	featureFlags: Record<string, boolean>;
	nonce: string;
	userData?: UserData;
	productTypes: Record<string, ProductData>;
	routeValues: Record<string, any>;

	// AppConfig
	BM_API_URL: string;
	BM_BASE_DOCS_URL: string;
	BM_BASE_URL: string;
	BM_BEFEKTETES_KOCKAZAT_URL: string;
	BM_BEFEKTETES_PORTFOLIO_URL: string;
	BM_ENVIRONMENT: "live" | "test" | "dev";
	BM_SENTRY_DSN: string;
	BM_WP_REST_API: string;
}

export class AppConfig {
	private readonly data: AppConfigData;
	private static instance: AppConfig;
	private readonly user;

	private constructor(data: AppConfigData) {
		this.data = data;
		this.user = new User(data.userData);
	}

	static getInstance(data: AppConfigData): AppConfig {
		if (!AppConfig.instance) {
			AppConfig.instance = new AppConfig(data);
		}
		return AppConfig.instance;
	}

	public getCurrentUser(): User {
		return this.user;
	}

	public getNonce(): string {
		return this.data.nonce;
	}

	public getBankmonitorUrl(path: string, params: Record<string, any> = {}): string {
		return buildUrl(this.data.BM_BASE_URL, path, params);
	}

	public getLeadSource(defaultSource = "BM"): string {
		const urlParams = getUrlParameters();
		if (urlParams.source) {
			return `${urlParams.source}`.toUpperCase();
		}
		if (urlParams.utm_campaign === "mailchimp" && urlParams.mc_cid) {
			return "EDM";
		}

		return defaultSource;
	}

	public getApiUrl(path: string, params: Record<string, any> = {}): string {
		return buildUrl(this.data.BM_API_URL, path, params);
	}

	public getWpRestApiUrl(path: string, params: Record<string, any> = {}): string {
		return buildUrl(this.data.BM_WP_REST_API, path, params);
	}

	/**
	 * Builds an SSR-friendly URL by combining a base URL, optional path, and query parameters.
	 *
	 * If SSR mode is detected, the base URL is replaced with a placeholder
	 * (e.g., `##BM_BASE_URL##`; see `config/webpack/plugin-prerender.js` for details).
	 * Otherwise, the actual base URL is taken from the configuration.
	 *
	 * @param base  The key identifying the base URL in the configuration.
	 * @param path  An optional path to append to the base URL (defaults to an empty string).
	 * @param params An optional record of query parameters to be appended (defaults to an empty object).
	 * @returns The constructed URL as a string.
	 */
	public buildSsrFriendlyUrl(base: BaseUrlType, path = "", params: Record<string, unknown> = {}): string {
		const urlBase = this.isSsr() ? `##${base}##` : this.data[base];
		const urlPath = addParameterToURL(path, params);

		return urlBase + urlPath;
	}

	public getEnvironment(): "live" | "test" | "dev" {
		return this.data.BM_ENVIRONMENT;
	}

	public getSentryDsn(): string {
		return this.data.BM_SENTRY_DSN;
	}

	public isSsr(): boolean {
		return window.BM_RENDER_TYPE === "ssr";
	}

	public isPartner(): boolean {
		return this.data.isPartner === "1";
	}

	public showPoweredByBankmonitor(): boolean {
		return this.data.showPoweredBy === "1";
	}

	public showBmDiscounts(): boolean {
		return this.data.bmDiscounts === "1";
	}

	public showConsultantQuotes(): boolean {
		return true;
	}

	public showHasDataPage(): boolean {
		return true;
	}

	public getRouteValues(): Record<string, any> {
		return this.data.routeValues;
	}

	public getProductData(type: string): ProductData {
		return this.data.productTypes[type];
	}

	public getMinWage(): number {
		return this.data.minWage;
	}

	public getAbVersion(): "A" | "B" {
		return this.data.abVersion || "A";
	}

	public getFeatureFlags(): Record<string, boolean> {
		return this.data.featureFlags;
	}

	public getContactEmail(): string {
		return this.data.contactEmail;
	}
}
